import { fetchBaseQuery, BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';
import { dotenv } from '@/utils/constants/env';
import { refreshSession, clearSession } from '@/redux/slices/sessionStateSlice';
import type { RootState } from './store';

const mutex = new Mutex();

const rawBaseQuery = fetchBaseQuery({
  baseUrl: dotenv.API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).sessionState.accessToken;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401) {
    const reqUrl = typeof args === 'string' ? args : (args as FetchArgs).url ?? '';
    if (reqUrl.includes('/auth/login') || reqUrl.includes('/auth/google') || reqUrl.includes('/auth/register')) {
      return result;
    }
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const state = api.getState() as RootState;
        const refreshToken = state.sessionState.refreshToken;
        if (!refreshToken) {
          api.dispatch(clearSession());
          return result;
        }
        const refreshResult = await rawBaseQuery(
          {
            url: '/api/v1/auth/refresh',
            method: 'POST',
            body: { refresh_token: refreshToken },
          },
          api,
          extraOptions
        );
        if (refreshResult.error) {
          const errData = (refreshResult.error as { data?: { message_code?: number } })?.data;
          const code = errData?.message_code;
          api.dispatch(clearSession());
          if (code === 1121) {
            window.location.href = '/suspended/tenant';
          } else if (code === 1120 || code === 1007) {
            window.location.href = '/suspended/account';
          }
          return result;
        }
        if (refreshResult.data) {
          const { data } = refreshResult.data as {
            data: {
              access_token: string;
              refresh_token?: string;
              roles?: string[];
              permissions?: string[];
              subscription_status?: string;
            };
          };
          api.dispatch(
            refreshSession({
              accessToken: data.access_token,
              refreshToken: data.refresh_token,
              roles: data.roles,
              permissions: data.permissions,
              subscription_status: data.subscription_status,
            })
          );
          result = await rawBaseQuery(args, api, extraOptions);
        } else {
          api.dispatch(clearSession());
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await rawBaseQuery(args, api, extraOptions);
    }
  }
  return result;
};
